/**
 * BarcodeAssignment — Full-screen productivity tool for the initial
 * ~2000-SKU EAN seed + ongoing barcode assignment on newly-created
 * products.
 *
 * Design goal: one operator, one scanner, one Enter-key press per
 * product. No mouse, no page navigation, no save button.
 *
 * Flow:
 *   1. On mount → fetch first N unassigned products (filtered by brand
 *      / category / supplier if set). Keep them in a client-side queue
 *      so scanning is instant; refill in the background when the queue
 *      dips below a threshold.
 *   2. The active product is queue[0]. Its image + SKU + name + brand
 *      are prominently displayed; the barcode <Input> is auto-focused
 *      and stays focused across renders.
 *   3. Operator scans (or types). On Enter or after a short debounce we
 *      call PATCH /products/:id/barcode with strict EAN validation:
 *        - success: beep + toast, shift queue, advance to next product.
 *        - 409:     surface the conflicting product with a "review"
 *                   link that opens the product-edit page in a NEW tab
 *                   (never navigate away).
 *        - 400:     inline error, don't advance, refocus input.
 *   4. Undo Last (30-second window) reverts the previous assignment by
 *      calling DELETE /products/:id/barcode and reinserting the product
 *      at position 0 in the queue.
 *   5. Skip Product moves the current product to the back of the queue
 *      so the operator can come back later.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Barcode, RotateCcw, SkipForward, X, CheckCircle2, AlertTriangle,
    Filter as FilterIcon, ChevronDown, ChevronUp, Camera,
} from 'lucide-react';

import api, { productsAPI } from '../lib/api';
import { ProductImage } from '../components/ProductImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import BarcodeScannerOverlay from '../components/scanner/BarcodeScannerOverlay';

// 30-second undo window (matches the spec)
const UNDO_WINDOW_MS = 30_000;

// How many pending products we prefetch into the client-side queue.
const QUEUE_PREFETCH = 25;
// Refill when the local queue falls below this.
const QUEUE_REFILL_TRIGGER = 5;

// Success beep — 880 Hz sine wave, ~100 ms. Uses WebAudio so no asset
// download is needed and it works on iOS Safari.
let _audioCtx;
const beep = () => {
    try {
        if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const ctx = _audioCtx;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.1);
    } catch { /* audio blocked — silent is acceptable */ }
};

const BarcodeAssignment = () => {
    // -----------------------------------------------------------------
    // Filters
    // -----------------------------------------------------------------
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [filters, setFilters] = useState({
        brand_id: '',
        category_id: '',
        supplier_id: '',
    });

    // -----------------------------------------------------------------
    // Queue + stats
    // -----------------------------------------------------------------
    const [queue, setQueue] = useState([]);
    const [stats, setStats] = useState({ total: 0, assigned: 0, remaining: 0, assigned_today: 0 });
    const [loading, setLoading] = useState(true);
    // `hasMore` gates the auto-refill effect so a filter that returns
    // zero rows doesn't kick off a refetch loop. Reset to true whenever
    // filters change (same place cursor is reset).
    const [hasMore, setHasMore] = useState(true);
    const [scanValue, setScanValue] = useState('');
    const [conflict, setConflict] = useState(null); // {message, conflict_product}
    const [assigning, setAssigning] = useState(false);
    const [lastAssignment, setLastAssignment] = useState(null); // {product_id, at}
    const [scannerOpen, setScannerOpen] = useState(false);      // phone-camera scanner
    // Filters collapsed by default on mobile — checked on mount so the
    // panel opens automatically on desktop when the user starts assigning.
    const [filtersOpen, setFiltersOpen] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
    );
    const inputRef = useRef(null);
    const cursorSkuRef = useRef(null);

    // Keep the barcode input focused whenever the active product changes.
    useEffect(() => {
        inputRef.current?.focus();
    }, [queue.length, conflict]);

    // -----------------------------------------------------------------
    // Fetch masters (brands / categories / suppliers) once on mount
    // -----------------------------------------------------------------
    useEffect(() => {
        Promise.all([
            api.get('/brands').then((r) => r.data).catch(() => []),
            api.get('/categories').then((r) => r.data).catch(() => []),
            api.get('/suppliers').then((r) => r.data.items ?? r.data).catch(() => []),
        ]).then(([b, c, s]) => {
            setBrands(b || []);
            setCategories(c || []);
            setSuppliers(s || []);
        });
    }, []);

    // -----------------------------------------------------------------
    // Fetch stats — polled after every save so the progress bar keeps
    // pace with the operator.
    // -----------------------------------------------------------------
    const refreshStats = useCallback(async () => {
        try {
            const { data } = await productsAPI.barcodeStats();
            setStats(data);
        } catch { /* stats are decorative — never block workflow */ }
    }, []);

    // -----------------------------------------------------------------
    // Queue refill from server. Called on mount, on filter change, and
    // whenever the client queue dips below the trigger.
    // -----------------------------------------------------------------
    const fetchMore = useCallback(async ({ reset = false } = {}) => {
        const params = {
            limit: QUEUE_PREFETCH,
            ...(filters.brand_id && { brand_id: filters.brand_id }),
            ...(filters.category_id && { category_id: filters.category_id }),
            ...(filters.supplier_id && { supplier_id: filters.supplier_id }),
            ...(!reset && cursorSkuRef.current && { cursor_sku: cursorSkuRef.current }),
        };
        setLoading(true);
        try {
            const { data } = await productsAPI.missingBarcode(params);
            const items = data.items || [];
            if (items.length) cursorSkuRef.current = items[items.length - 1].sku;
            // The server returned fewer items than we asked for → we've
            // exhausted the missing-barcode set for the current filters.
            // Flip `hasMore` so the auto-refill effect stops firing.
            setHasMore(items.length >= QUEUE_PREFETCH);
            setQueue((prev) => (reset ? items : [...prev, ...items]));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load products');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // Reset queue when filters change.
    useEffect(() => {
        cursorSkuRef.current = null;
        setHasMore(true);
        fetchMore({ reset: true });
        refreshStats();
    }, [fetchMore, refreshStats]);

    // Background refill — only when we know more results likely exist.
    useEffect(() => {
        if (queue.length <= QUEUE_REFILL_TRIGGER && !loading && hasMore) {
            fetchMore();
        }
    }, [queue.length, loading, hasMore, fetchMore]);

    // -----------------------------------------------------------------
    // Assignment flow
    // -----------------------------------------------------------------
    const active = queue[0];

    const advance = () => {
        setScanValue('');
        setConflict(null);
        setQueue((q) => q.slice(1));
    };

    const submitScan = async (raw) => {
        const code = (raw || scanValue || '').trim();
        if (!code || !active || assigning) return;
        setAssigning(true);
        setConflict(null);
        try {
            await productsAPI.assignBarcode(active.id, code);
            beep();
            toast.success(`${active.sku} → ${code}`);
            setLastAssignment({ product_id: active.id, at: Date.now() });
            refreshStats();
            advance();
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409 && typeof detail === 'object' && detail?.conflict_product) {
                setConflict({
                    message: detail.message,
                    conflict_product: detail.conflict_product,
                });
                // Beep pattern: 2 short low tones to signal error
                try {
                    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                    const ctx = _audioCtx;
                    [0, 0.15].forEach((delay) => {
                        const o = ctx.createOscillator();
                        const g = ctx.createGain();
                        o.type = 'sine';
                        o.frequency.value = 220;
                        g.gain.setValueAtTime(0.2, ctx.currentTime + delay);
                        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.08);
                        o.connect(g).connect(ctx.destination);
                        o.start(ctx.currentTime + delay);
                        o.stop(ctx.currentTime + delay + 0.08);
                    });
                } catch { /* silent */ }
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Failed to assign barcode');
            }
            setScanValue('');
            inputRef.current?.focus();
        } finally {
            setAssigning(false);
        }
    };

    // Undo last assignment — only allowed within 30 seconds.
    const undoLast = async () => {
        if (!lastAssignment) return;
        const age = Date.now() - lastAssignment.at;
        if (age > UNDO_WINDOW_MS) {
            toast.error('Undo window (30s) has passed');
            setLastAssignment(null);
            return;
        }
        try {
            await productsAPI.clearBarcode(lastAssignment.product_id);
            // Refetch the product's slim record so we can put it back at
            // the head of the queue for immediate re-scan.
            const { data: fresh } = await productsAPI.get(lastAssignment.product_id);
            const slim = {
                id: fresh.id, sku: fresh.sku, name: fresh.name,
                brand: fresh.brand, brand_id: fresh.brand_id,
                category: fresh.category, category_id: fresh.category_id,
                supplier: fresh.supplier, supplier_id: fresh.supplier_id,
                barcode: fresh.barcode || '', image_url: fresh.image_url || '',
                price: fresh.price, stock_qty: fresh.stock_qty, unit: fresh.unit,
            };
            setQueue((q) => [slim, ...q]);
            setLastAssignment(null);
            refreshStats();
            toast.success('Undone — rescan ready');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Undo failed');
        }
    };

    const skip = () => {
        if (!active) return;
        setQueue((q) => (q.length > 1 ? [...q.slice(1), q[0]] : q));
        setScanValue('');
        setConflict(null);
    };

    // Keep the latest `submitScan` reachable by the camera callback
    // (which is memoised with a stable identity for scanner reuse).
    const submitScanRef = useRef(submitScan);
    useEffect(() => { submitScanRef.current = submitScan; });

    // -----------------------------------------------------------------
    // Phone-camera scan → same code path as manual typing
    // -----------------------------------------------------------------
    const handleCameraDetected = useCallback(
        (code) => {
            setScannerOpen(false);
            // Delegate to submitScan through scanValue so operator sees
            // the code that was captured (helps debugging).
            setScanValue(code);
            // small delay so React commits scanValue before we read it
            setTimeout(() => submitScanRef.current?.(code), 30);
        },
        [],
    );

    // -----------------------------------------------------------------
    // Derived UI values
    // -----------------------------------------------------------------
    const undoEnabled =
        lastAssignment && Date.now() - lastAssignment.at <= UNDO_WINDOW_MS;
    const progressPct = stats.total
        ? Math.round((stats.assigned / stats.total) * 100)
        : 0;

    const resetFilters = () =>
        setFilters({ brand_id: '', category_id: '', supplier_id: '' });

    // -----------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------
    return (
        // Container hugs the viewport width on mobile (via -mx-4 to neutralise
        // Layout's p-4) but flows normally VERTICALLY so it sits BELOW the
        // fixed mobile hamburger bar. Older code used `-mt-4` which pulled
        // the black top bar up behind the hamburger and hid the progress
        // strip on Android portrait — that's why it only appeared on landscape.
        <div
            className="-mx-4 md:mx-0 flex flex-col md:h-[calc(100vh-6rem)] bg-[#FBF8F6]"
            data-testid="barcode-assignment"
        >
            <TopBar
                stats={stats}
                progressPct={progressPct}
                onExit={() => window.history.back()}
                filtersOpen={filtersOpen}
                onToggleFilters={() => setFiltersOpen((v) => !v)}
            />
            {/* Mobile: filters stack on top and are collapsible. Desktop:
                fixed 280 px sidebar. The grid switches at md (768 px). */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 md:gap-4 p-3 md:p-4 overflow-y-auto md:overflow-hidden">
                <FiltersPanel
                    brands={brands}
                    categories={categories}
                    suppliers={suppliers}
                    filters={filters}
                    onChange={setFilters}
                    onReset={resetFilters}
                    isOpen={filtersOpen}
                    onToggle={() => setFiltersOpen((v) => !v)}
                />
                <div className="md:overflow-auto min-w-0">
                    {active ? (
                        <ActiveProductCard
                            product={active}
                            inputRef={inputRef}
                            scanValue={scanValue}
                            setScanValue={setScanValue}
                            onSubmit={submitScan}
                            onOpenCamera={() => setScannerOpen(true)}
                            assigning={assigning}
                            conflict={conflict}
                            dismissConflict={() => { setConflict(null); inputRef.current?.focus(); }}
                            queueLength={queue.length}
                            undoEnabled={undoEnabled}
                            onUndo={undoLast}
                            onSkip={skip}
                        />
                    ) : (
                        <EmptyState loading={loading} onResetFilters={resetFilters} />
                    )}
                </div>
            </div>
            <BarcodeScannerOverlay
                open={scannerOpen}
                onDetected={handleCameraDetected}
                onClose={() => setScannerOpen(false)}
            />
        </div>
    );
};

// ----- Sub-components -----------------------------------------------------

const TopBar = ({ stats, progressPct, onExit, filtersOpen, onToggleFilters }) => (
    <div className="bg-[#1A1A1A] text-white px-3 md:px-4 py-2.5 md:py-3 flex-shrink-0">
        {/* Mobile: two rows so stats + progress don't get squeezed. Desktop: single row. */}
        <div className="flex items-center gap-2 md:gap-4">
            <Barcode size={18} className="text-[#C08478] flex-shrink-0" />
            <div className="hidden md:flex flex-1 items-center gap-6 text-xs min-w-0">
                <StatChip label="Total" value={stats.total} />
                <StatChip label="Assigned" value={stats.assigned} tone="ok" />
                <StatChip label="Remaining" value={stats.remaining} tone="warn" />
                <StatChip label="Today" value={stats.assigned_today} />
                <div className="flex-1 min-w-[120px] max-w-md">
                    <Progress value={progressPct} className="h-2 bg-white/10" />
                </div>
                <span className="font-mono text-[#C08478]" data-testid="progress-pct-desktop">{progressPct}%</span>
            </div>
            {/* Mobile-only compact stats — just Assigned / Remaining / % */}
            <div className="md:hidden flex-1 flex items-center gap-3 text-[11px] min-w-0">
                <StatChip label="Done" value={stats.assigned} tone="ok" />
                <StatChip label="Left" value={stats.remaining} tone="warn" />
                <div className="flex-1 min-w-0">
                    <Progress value={progressPct} className="h-1.5 bg-white/10" />
                </div>
                <span className="font-mono text-[#C08478]" data-testid="progress-pct">{progressPct}%</span>
            </div>
            <Button
                size="sm"
                variant="ghost"
                onClick={onToggleFilters}
                data-testid="toggle-filters-btn"
                className="md:hidden text-white hover:bg-white/10 gap-1 px-2 h-8"
            >
                <FilterIcon size={14} />
                {filtersOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </Button>
            <Button
                size="sm"
                variant="ghost"
                onClick={onExit}
                data-testid="exit-assignment-btn"
                className="text-white hover:bg-white/10 gap-1 px-2 md:px-3 h-8"
            >
                <X size={14} /> <span className="hidden md:inline">Exit</span>
            </Button>
        </div>
    </div>
);

const StatChip = ({ label, value, tone = '' }) => (
    <div className="flex flex-col leading-tight">
        <span className="text-white/50 text-[10px] uppercase tracking-wider">{label}</span>
        <span className={`font-mono font-semibold ${
            tone === 'ok' ? 'text-emerald-400'
            : tone === 'warn' ? 'text-amber-400'
            : 'text-white'}`}
            data-testid={`stat-${label.toLowerCase()}`}
        >
            {value}
        </span>
    </div>
);

const FiltersPanel = ({ brands, categories, suppliers, filters, onChange, onReset, isOpen, onToggle }) => {
    const update = (k, v) => onChange({ ...filters, [k]: v === '__all__' ? '' : v });
    // Filter panel is always visible on md+ regardless of `isOpen` — the
    // toggle is a mobile-only affordance. The `md:block` override keeps
    // desktop unchanged when the operator has collapsed it on mobile.
    return (
        <Card
            data-testid="filters-panel"
            className={`h-fit ${isOpen ? 'block' : 'hidden'} md:block`}
        >
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold flex items-center gap-2">
                        <FilterIcon size={14} /> Filters
                    </span>
                    <div className="flex items-center gap-1">
                        <Button
                            size="sm" variant="ghost"
                            onClick={onReset}
                            data-testid="reset-filters-btn"
                            className="h-8 text-xs"
                        >
                            Reset
                        </Button>
                        <Button
                            size="sm" variant="ghost"
                            onClick={onToggle}
                            data-testid="close-filters-btn"
                            className="md:hidden h-8 text-xs gap-1"
                        >
                            <ChevronUp size={14} /> Hide
                        </Button>
                    </div>
                </div>
                <FilterSelect
                    label="Brand" testId="brand-filter"
                    value={filters.brand_id || '__all__'}
                    onValueChange={(v) => update('brand_id', v)}
                    options={brands}
                />
                <FilterSelect
                    label="Category" testId="category-filter"
                    value={filters.category_id || '__all__'}
                    onValueChange={(v) => update('category_id', v)}
                    options={categories}
                />
                <FilterSelect
                    label="Supplier" testId="supplier-filter"
                    value={filters.supplier_id || '__all__'}
                    onValueChange={(v) => update('supplier_id', v)}
                    options={suppliers}
                />
                <p className="text-[11px] text-gray-500 pt-2 border-t">
                    Showing products <span className="font-medium">missing a barcode</span>.
                    Assigned products drop off automatically.
                </p>
            </CardContent>
        </Card>
    );
};

const FilterSelect = ({ label, value, onValueChange, options, testId }) => (
    <div>
        <label className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">{label}</label>
        <Select value={value} onValueChange={onValueChange}>
            <SelectTrigger className="mt-1" data-testid={testId}>
                <SelectValue placeholder={`All ${label.toLowerCase()}s`} />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="__all__">All {label.toLowerCase()}s</SelectItem>
                {options.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

const ActiveProductCard = ({
    product, inputRef, scanValue, setScanValue, onSubmit, onOpenCamera, assigning,
    conflict, dismissConflict, queueLength, undoEnabled, onUndo, onSkip,
}) => {
    const handleKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
        }
    };
    return (
        <Card data-testid="active-product-card" className="max-w-3xl mx-auto">
            <CardContent className="p-4 md:p-6 space-y-4 md:space-y-5">
                {/* Image + meta — stack on mobile, side-by-side ≥sm */}
                <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] md:grid-cols-[220px_1fr] gap-4 md:gap-6">
                    <ProductImage
                        product={product}
                        alt={product.name}
                        className="w-full aspect-square bg-white rounded-lg border max-w-[240px] mx-auto sm:mx-0"
                        testid="active-product-image"
                    />
                    <div className="space-y-2 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500">
                            <span>SKU</span>
                            <span className="font-mono font-semibold text-gray-700" data-testid="active-sku">{product.sku}</span>
                            {product.brand && (
                                <>
                                    <span className="text-gray-300">·</span>
                                    <span className="font-medium" data-testid="active-brand">{product.brand}</span>
                                </>
                            )}
                        </div>
                        <h2 className="text-lg md:text-xl font-semibold leading-snug break-words" data-testid="active-name">
                            {product.name}
                        </h2>
                        <p className="text-xs text-gray-500">
                            Barcode status:{' '}
                            {product.barcode
                                ? <span className="text-emerald-600 font-medium">Assigned — <span className="font-mono">{product.barcode}</span></span>
                                : <span className="text-amber-600 font-medium">Missing</span>}
                        </p>
                        <p className="text-[11px] text-gray-400">
                            {queueLength} products queued (auto-refill).
                        </p>
                    </div>
                </div>

                {conflict ? (
                    <div className="rounded-lg border border-[#E76F51]/40 bg-[#FBF3EF] p-3 md:p-4 space-y-2" data-testid="conflict-panel">
                        <div className="flex items-center gap-2 text-[#E76F51] font-medium">
                            <AlertTriangle size={16} /> Duplicate barcode
                        </div>
                        <p className="text-sm text-gray-700">{conflict.message}</p>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                            <Link
                                to={`/products?highlight=${conflict.conflict_product.id}`}
                                target="_blank"
                                rel="noopener"
                                className="text-[#C08478] hover:underline font-medium"
                                data-testid="review-conflict-link"
                            >
                                Review {conflict.conflict_product.sku}
                            </Link>
                            <Button size="sm" variant="outline" onClick={dismissConflict} data-testid="dismiss-conflict-btn">
                                Rescan
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <label className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                            Scan barcode
                        </label>
                        {/* Camera button on all sizes; on mobile the input
                            grows to full-width so the touch target is
                            comfortable and never wraps under the buttons. */}
                        <div className="flex items-center gap-2">
                            <Input
                                ref={inputRef}
                                value={scanValue}
                                onChange={(e) => setScanValue(e.target.value)}
                                onKeyDown={handleKey}
                                placeholder="Scan or type EAN…"
                                className="font-mono text-lg h-14 min-w-0"
                                autoFocus
                                autoComplete="off"
                                inputMode="numeric"
                                data-testid="barcode-input"
                                disabled={assigning}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={onOpenCamera}
                                title="Scan with phone camera"
                                aria-label="Scan with phone camera"
                                data-testid="assign-camera-btn"
                                disabled={assigning}
                                className="h-14 w-14 shrink-0"
                            >
                                <Camera size={20} />
                            </Button>
                            <Button
                                size="lg"
                                onClick={() => onSubmit()}
                                disabled={assigning || !scanValue.trim()}
                                data-testid="submit-scan-btn"
                                className="h-14 bg-[#1A1A1A] hover:bg-[#C08478] text-white gap-2 px-3 md:px-6"
                            >
                                <CheckCircle2 size={18} />
                                <span className="hidden sm:inline">Save</span>
                            </Button>
                        </div>
                        <p className="text-[11px] text-gray-500">
                            Press Enter after each scan — the next product loads automatically.
                            Hardware scanners just work (they type into this field). On phone, tap the camera icon.
                        </p>
                    </div>
                )}

                {/* Actions — buttons expand full-width on mobile so touch
                    targets meet the ≥44 px iOS/Android guideline. */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t">
                    <Button
                        variant="outline"
                        onClick={onUndo}
                        disabled={!undoEnabled}
                        data-testid="undo-last-btn"
                        className="gap-1 h-11 sm:h-9"
                    >
                        <RotateCcw size={14} /> Undo last (30s)
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={onSkip}
                        data-testid="skip-product-btn"
                        className="gap-1 text-gray-600 h-11 sm:h-9"
                    >
                        <SkipForward size={14} /> Skip product
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

const EmptyState = ({ loading, onResetFilters }) => (
    <div className="h-full flex items-center justify-center" data-testid="empty-state">
        <Card className="max-w-md">
            <CardContent className="p-8 text-center space-y-3">
                <CheckCircle2 size={40} className="text-emerald-500 mx-auto" />
                <h3 className="text-lg font-semibold">
                    {loading ? 'Loading products…' : 'All done!'}
                </h3>
                <p className="text-sm text-gray-500">
                    {loading
                        ? 'Fetching the next batch of products awaiting a barcode.'
                        : 'No products match the current filters without a barcode. Adjust the filters or celebrate — every SKU is scanned.'}
                </p>
                {/* Always render the escape hatch so a slow/stuck fetch
                    never traps the operator without a way to reset. */}
                <Button variant="outline" onClick={onResetFilters} data-testid="reset-empty-btn">
                    Reset filters
                </Button>
            </CardContent>
        </Card>
    </div>
);

export default BarcodeAssignment;
